import { useState } from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  ToggleButtonGroup, 
  ToggleButton,
  Paper
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  PeopleOutlined as PeopleIcon
} from '@mui/icons-material';
import { RegionData } from '@/types/dashboard';
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";
import indiaTopoJSON from '../../../public/data/geography-data/india.topo.json';

// Constants for the India map
const INDIA_TOPO_JSON = indiaTopoJSON;

// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD' // Darker blue for hover states
};

interface MiniRegionMapProps {
  regionData: Record<string, RegionData>;
  onRegionSelect: (region: string) => void;
  compact?: boolean;
  selectedRegion?: string;
}

export function MiniRegionMap({ 
  regionData, 
  onRegionSelect, 
  compact = false, 
  selectedRegion 
}: MiniRegionMapProps) {
  const theme = useTheme();
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [viewBy, setViewBy] = useState<'leads' | 'conversion'>('conversion');
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'leads' | 'conversion' | null) => {
    if (newView !== null) {
      setViewBy(newView);
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setCursorPosition({ x: event.clientX, y: event.clientY });
  };

  const getRegionColor = (regionCode: string) => {
    if (!regionData[regionCode]) {
      return theme.palette.grey[300]; // Default color for regions without data
    }

    if (viewBy === 'conversion') {
      const conversionRate = regionData[regionCode].conversionRate;
      
      // Color based on conversion rate
      if (conversionRate > 60) return '#4CAF50'; // Green for high conversion
      if (conversionRate >= 40) return '#FF6E00'; // Orange for medium conversion
      return '#F44336'; // Red for low conversion
    } else {
      // Color based on Total Base
      const totalLeads = regionData[regionCode].totalLeads;
      const maxLeads = Math.max(...Object.values(regionData).map(data => data.totalLeads));
      
      // Normalize the value between 0 and 1
      const normalizedValue = totalLeads / maxLeads;
      
      // Use the same color scheme as conversion but for leads
      if (normalizedValue > 0.7) return '#4CAF50'; // Green for high leads
      if (normalizedValue > 0.4) return '#FF6E00'; // Orange for medium leads
      return '#F44336'; // Red for low leads
    }
  };

  const getRegionOpacity = (regionCode: string) => {
    if (hoveredRegion === regionCode || selectedRegion === regionCode) return 1;
    if (hoveredRegion !== null || selectedRegion) return 0.5;
    return 0.8;
  };

  const getRegionValue = (regionCode: string) => {
    if (!regionData[regionCode]) return '';
    
    if (viewBy === 'conversion') {
      return `${regionData[regionCode].conversionRate.toFixed(0)}%`;
    } else {
      const totalLeads = regionData[regionCode].totalLeads;
      return totalLeads >= 1000 ? `${Math.floor(totalLeads / 1000)}k` : totalLeads.toString();
    }
  };

  const getRegionStyles = (stateCode: string) => ({
    default: {
      fill: getRegionColor(stateCode),
      stroke: selectedRegion === stateCode ? CHOLA_COLORS.secondary : 
              regionData[stateCode] ? CHOLA_COLORS.lightBlue : '#999',
      strokeWidth: selectedRegion === stateCode ? 2 :
                 regionData[stateCode] ? 0.5 : 0.3,
      outline: "none",
      cursor: regionData[stateCode] ? 'pointer' : 'default',
      transition: 'all 0.3s ease'
    },
    hover: {
      fill: regionData[stateCode] ? CHOLA_COLORS.lightBlue : getRegionColor(stateCode),
      stroke: selectedRegion === stateCode ? CHOLA_COLORS.secondary :
              regionData[stateCode] ? CHOLA_COLORS.primary : '#999',
      strokeWidth: selectedRegion === stateCode ? 2.5 :
                 regionData[stateCode] ? 1 : 0.3,
      outline: "none",
      filter: regionData[stateCode] ? 'brightness(1.1)' : 'none',
      transform: regionData[stateCode] ? 'translateY(-1px)' : 'none',
      transition: 'all 0.3s ease'
    },
    pressed: {
      fill: regionData[stateCode] ? CHOLA_COLORS.lightBlue : getRegionColor(stateCode),
      stroke: selectedRegion === stateCode ? CHOLA_COLORS.secondary :
              regionData[stateCode] ? CHOLA_COLORS.primary : '#999',
      strokeWidth: selectedRegion === stateCode ? 2 :
                 regionData[stateCode] ? 1 : 0.3,
      outline: "none"
    }
  });

  return (
    <Box 
      sx={{ width: '100%', height: '100%', position: 'relative', pb: 0 }}
      onMouseMove={handleMouseMove}
    >
      {/* View toggle buttons */}
      <Box sx={{ 
        position: 'absolute', 
        top: -6, 
        right: 2, 
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <ToggleButtonGroup
          value={viewBy}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            height: 24,
            '& .MuiToggleButton-root': {
              px: 1,
              py: 0,
              minWidth: 'auto',
              textTransform: 'none',
              fontSize: '0.65rem',
              fontWeight: 500,
              color: '#475569',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: `${CHOLA_COLORS.primary}15`,
                color: CHOLA_COLORS.primary,
                '&:hover': {
                  backgroundColor: `${CHOLA_COLORS.primary}25`,
                },
              },
            },
            '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
              borderRadius: '4px 0 0 4px',
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderRadius: '0 4px 4px 0',
            },
          }}
        >
          <ToggleButton value="conversion">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>NOP Conversion</Typography>
            </Box>
          </ToggleButton>
          {/* <ToggleButton value="leads">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>Leads</Typography>
            </Box>
          </ToggleButton> */}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ 
        height: '100%', 
        width: '100%', 
        position: 'relative',
        mt: 0,
        pl: 0,
        pr: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 1050,
            center: [78.5, 21.5]
          }}
          style={{
            width: "105%",
            height: "95%", 
            margin: "-5% -2.5% 0 -2.5%"
          }}
        >
          <Geographies geography={INDIA_TOPO_JSON}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateCode = geo.id;
                const stateName = geo.properties.name;
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => {
                      setHoveredRegion(stateCode);
                    }}
                    onMouseLeave={() => {
                      setHoveredRegion(null);
                    }}
                    onClick={() => {
                      if (regionData[stateCode]) {
                        onRegionSelect(stateCode);
                      }
                    }}
                    style={getRegionStyles(stateCode)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </Box>
      
      {/* Legend */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 1, 
        right: 1, 
        bgcolor: 'rgba(255,255,255,0.9)',
        p: 0.5,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        fontSize: '0.7rem'
      }}>
        {viewBy === 'conversion' ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#4CAF50', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>&gt;60%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#FF6E00', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>40-60%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#F44336', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>&lt;40%</Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#4CAF50', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>High</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#FF6E00', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Medium</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 8, height: 8, bgcolor: '#F44336', mr: 0.5, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Low</Typography>
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Tooltip for hovered region */}
      {hoveredRegion && regionData[hoveredRegion] && (
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            top: cursorPosition.y + 20,
            left: cursorPosition.x + 10,
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderRadius: 2,
            minWidth: 220,
            border: `2px solid ${CHOLA_COLORS.primary}`,
            zIndex: 1000,
            pointerEvents: 'none',
            transform: 'translate(-50%, 0)',
            transition: 'all 0.1s ease',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -8,
              left: '50%',
              transform: 'translateX(-50%)',
              border: '8px solid transparent',
              borderBottomColor: CHOLA_COLORS.primary,
              zIndex: 1001
            }
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1.5, 
              color: CHOLA_COLORS.primary, 
              fontWeight: 600,
              borderBottom: `1px solid ${CHOLA_COLORS.primary}20`,
              pb: 1
            }}
          >
            {hoveredRegion}
          </Typography>
          {regionData[hoveredRegion] ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Base NOP:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {regionData[hoveredRegion].totalLeads?.toLocaleString() || '0'} 
                  {(() => {
                    const totalAllLeads = Object.values(regionData).reduce((sum, region) => sum + (region.totalLeads || 0), 0);
                    const percentage = ((regionData[hoveredRegion].totalLeads || 0) / totalAllLeads * 100);
                    return ` (${percentage.toFixed(1)}%)`;
                  })()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Renewed NOP:</Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: CHOLA_COLORS.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {regionData[hoveredRegion].converted?.toLocaleString() || '0'} 
                  ({(regionData[hoveredRegion].conversionRate || 0).toFixed(1)}%)
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No data available
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
} 